// HTTP resource-state error codes — 404 (not found), 409 (conflict), 410 (gone).
// Split out of auth.constants.ts (TO-591) to keep each constant file a cohesive,
// decomposable group under the module-size cap. Re-exported through the same
// `@today/types` barrel, so importers are unaffected.

/** Not-found codes for 404 responses. */
export enum NotFoundErrorCode {
	/** Default: a generic missing resource. */
	NOT_FOUND = 'NOT_FOUND',
	/** The tenant has no current SCIM token to revoke (TO-379 scim-connection REQ-003). */
	SCIM_TOKEN_NOT_FOUND = 'SCIM_TOKEN_NOT_FOUND',
	/** No saved attribute-mapping version has that number (TO-377 REQ-006). */
	IDP_MAPPING_VERSION_NOT_FOUND = 'IDP_MAPPING_VERSION_NOT_FOUND',
	/** The provider has no captured sample, or it expired (TO-377 REQ-007). */
	IDP_MAPPING_SAMPLE_NOT_FOUND = 'IDP_MAPPING_SAMPLE_NOT_FOUND',
	/** The target user is absent (or soft-deleted) in the resolved tenant (TO-307). */
	USER_NOT_FOUND = 'USER_NOT_FOUND',
	/**
	 * A cross-tenant caller named a slug that is no live tenant (TO-307), or no
	 * non-deleted tenant has the addressed id (TO-306 REQ-009).
	 */
	TENANT_NOT_FOUND = 'TENANT_NOT_FOUND',
	/** A per-context seeding retry named a context that is not in the seeder registry (TO-655 REQ-015). */
	TENANT_SEED_CONTEXT_NOT_FOUND = 'TENANT_SEED_CONTEXT_NOT_FOUND',
	/**
	 * No invitation matches the link token in the addressed tenant (or it is
	 * another user's), or a revoke found no outstanding invitation (TO-309).
	 */
	INVITATION_NOT_FOUND = 'INVITATION_NOT_FOUND',
	/**
	 * The file does not exist, belongs to another tenant, is soft-deleted, or the
	 * caller does not own it (TO-324 REQ-007). Deliberately the SAME code for all
	 * four, and never a 403: existence must not leak.
	 */
	FILE_NOT_FOUND = 'FILE_NOT_FOUND',
	/** The multipart session does not exist, is another tenant's, or is not the caller's (TO-324 REQ-010). */
	FILE_MULTIPART_SESSION_NOT_FOUND = 'FILE_MULTIPART_SESSION_NOT_FOUND',
	/** The product is absent, soft-deleted, or another tenant's (TO-415). */
	PRODUCT_NOT_FOUND = 'PRODUCT_NOT_FOUND',
	/** The category is absent, soft-deleted, or another tenant's (TO-415). */
	PRODUCT_CATEGORY_NOT_FOUND = 'PRODUCT_CATEGORY_NOT_FOUND',
	/** The product has no live translation for the locale, or none at all (TO-415). */
	PRODUCT_TRANSLATION_NOT_FOUND = 'PRODUCT_TRANSLATION_NOT_FOUND',
	/** The identity provider is absent or belongs to another tenant (TO-410). */
	IDP_NOT_FOUND = 'IDP_NOT_FOUND',
	/** The offer is absent, soft-deleted, or another tenant's (TO-339). */
	OFFER_NOT_FOUND = 'OFFER_NOT_FOUND',
	/**
	 * The API key is absent, soft-deleted, another user's or another tenant's
	 * (TO-516 REQ-006). The same code for all four, never a 403.
	 */
	API_KEY_NOT_FOUND = 'API_KEY_NOT_FOUND',
	/**
	 * A customer referenced on create is absent (or soft-deleted) in the tenant
	 * (TO-339). Same string as `CustomerErrorCode.NOT_FOUND` so the offer surface
	 * can answer `_404(CUSTOMER_NOT_FOUND)` without importing customer's enum.
	 */
	CUSTOMER_NOT_FOUND = 'CUSTOMER_NOT_FOUND',
	/** `{fileType}` is not one of the five logical files (TO-391). */
	FILE_TYPE_NOT_FOUND = 'FILE_TYPE_NOT_FOUND',
	/** `{key}` is malformed or names no import definition of the target tenant (TO-414). */
	IMPORT_DEFINITION_NOT_FOUND = 'IMPORT_DEFINITION_NOT_FOUND',
	/** `{version}` is malformed or names no version of the import definition (TO-414). */
	IMPORT_DEFINITION_VERSION_NOT_FOUND = 'IMPORT_DEFINITION_VERSION_NOT_FOUND',
	/** `{connectorId}` is not a non-deleted SFTP connector of the target tenant (TO-393). */
	CONNECTOR_NOT_FOUND = 'CONNECTOR_NOT_FOUND',
	/** The import is absent, another tenant's, or (on complete) not an upload (TO-392). */
	IMPORT_EXECUTION_NOT_FOUND = 'IMPORT_EXECUTION_NOT_FOUND',
	/** An outbound webhook subscription is absent, soft-deleted or another tenant's (TO-381). */
	WEBHOOK_SUBSCRIPTION_NOT_FOUND = 'WEBHOOK_SUBSCRIPTION_NOT_FOUND',
	/** An outbound delivery is absent or another tenant's (TO-381). */
	WEBHOOK_DELIVERY_NOT_FOUND = 'WEBHOOK_DELIVERY_NOT_FOUND',
	/** The erasure request is absent or another tenant's (TO-232 REQ-001). */
	ERASURE_NOT_FOUND = 'ERASURE_NOT_FOUND',
	/** The legal hold is absent or another tenant's (TO-232 REQ-001). */
	LEGAL_HOLD_NOT_FOUND = 'LEGAL_HOLD_NOT_FOUND',
	/** No (non-anonymised) customer of the tenant matches the subject (TO-232 REQ-002/102). */
	SUBJECT_NOT_FOUND = 'SUBJECT_NOT_FOUND',
	/** The subject access request is absent or another tenant's (TO-232 REQ-101). */
	SAR_NOT_FOUND = 'SAR_NOT_FOUND',
	/** A notification template is absent, soft-deleted or another tenant's (TO-140). */
	NOTIFICATION_TEMPLATE_NOT_FOUND = 'NOTIFICATION_TEMPLATE_NOT_FOUND',
}

/** Conflict codes for 409 responses. */
export enum ConflictErrorCode {
	/** Default: a generic uniqueness/constraint conflict. */
	CONFLICT = 'CONFLICT',
	/** The email already belongs to a non-deleted user IN THIS TENANT (TO-303 REQ-003). */
	EMAIL_TAKEN = 'EMAIL_TAKEN',
	/** The slug already belongs to another tenant (TO-306 REQ-004). */
	SLUG_TAKEN = 'SLUG_TAKEN',
	/** The tenant already has an active webhook endpoint for that provider (TO-323 REQ-002). */
	WEBHOOK_ENDPOINT_EXISTS = 'WEBHOOK_ENDPOINT_EXISTS',
	/** Rotate / test addressed a tenant that has no SSO configuration (TO-308). */
	IDP_NOT_CONFIGURED = 'IDP_NOT_CONFIGURED',
	/** An onboarding PATCH asked to move a step out of `complete` (TO-190 REQ-003). */
	ONBOARDING_STEP_TRANSITION_INVALID = 'ONBOARDING_STEP_TRANSITION_INVALID',
	/** Invitation issue/re-send for a user whose status is not `INVITED` (TO-309). */
	USER_ALREADY_ACTIVE = 'USER_ALREADY_ACTIVE',
	/** First-login against a revoked invitation (TO-309). */
	INVITATION_REVOKED = 'INVITATION_REVOKED',
	/** First-login against an invitation replaced by a later re-send (TO-309). */
	INVITATION_SUPERSEDED = 'INVITATION_SUPERSEDED',
	/** First-login after the invitation's `expiresAt` (TO-309). */
	INVITATION_EXPIRED = 'INVITATION_EXPIRED',
	/** First-login against an invitation that already completed one (TO-309). */
	INVITATION_ALREADY_USED = 'INVITATION_ALREADY_USED',
	/** The first-login path does not match the tenant's federation mode (TO-309). */
	LOGIN_METHOD_MISMATCH = 'LOGIN_METHOD_MISMATCH',
	/** Another tenant already holds a live record for the sending domain (TO-382). */
	SENDER_DOMAIN_CLAIMED = 'SENDER_DOMAIN_CLAIMED',
	/** Federated completion by an identity that does not carry the tenant's provider (TO-309). */
	FEDERATED_LOGIN_REQUIRED = 'FEDERATED_LOGIN_REQUIRED',
	/** Cognito refused to link the tenant's SAML identity to the invited user (TO-309). */
	FEDERATED_IDENTITY_CONFLICT = 'FEDERATED_IDENTITY_CONFLICT',
	/** Upload confirmation ran but no object exists at the file's storage key (TO-324 REQ-002). */
	FILE_NOT_UPLOADED = 'FILE_NOT_UPLOADED',
	/** Upload confirmation ran against a file that is not `PENDING` (TO-324 REQ-002). */
	FILE_ALREADY_CONFIRMED = 'FILE_ALREADY_CONFIRMED',
	/** A download URL or a reprocess was asked for a file that is not `AVAILABLE` (TO-324 REQ-003/REQ-011). */
	FILE_NOT_AVAILABLE = 'FILE_NOT_AVAILABLE',
	/** The multipart session's expiry has passed (TO-324 REQ-010). */
	FILE_MULTIPART_SESSION_EXPIRED = 'FILE_MULTIPART_SESSION_EXPIRED',
	/** The multipart session is already completed or aborted (TO-324 REQ-010). */
	FILE_MULTIPART_SESSION_NOT_OPEN = 'FILE_MULTIPART_SESSION_NOT_OPEN',
	/** The parts presented at completion do not match the parts S3 holds (TO-324 REQ-009). */
	FILE_MULTIPART_PARTS_MISMATCH = 'FILE_MULTIPART_PARTS_MISMATCH',
	/** The product code is already used in the tenant, soft-deleted rows included (TO-415). */
	PRODUCT_CODE_TAKEN = 'PRODUCT_CODE_TAKEN',
	/** The category code is already used in the tenant, soft-deleted rows included (TO-415). */
	PRODUCT_CATEGORY_CODE_TAKEN = 'PRODUCT_CATEGORY_CODE_TAKEN',
	/** The category is still referenced by a non-deleted product (TO-415). */
	PRODUCT_CATEGORY_IN_USE = 'PRODUCT_CATEGORY_IN_USE',
	/** The (from, to) status pair is not an allowed lifecycle move (TO-415). */
	PRODUCT_TRANSITION_NOT_ALLOWED = 'PRODUCT_TRANSITION_NOT_ALLOWED',
	/** The product's status changed between the read and the conditional update (TO-415). */
	PRODUCT_STATUS_CONFLICT = 'PRODUCT_STATUS_CONFLICT',
	/** Deleting a required translation of an ACTIVE product (TO-415). */
	PRODUCT_TRANSLATION_REQUIRED = 'PRODUCT_TRANSLATION_REQUIRED',
	/** A translation upsert lost a race to a concurrent write twice; the client may retry (TO-415). */
	PRODUCT_TRANSLATION_CONFLICT = 'PRODUCT_TRANSLATION_CONFLICT',
	/** The identity-provider code is already used in the tenant (TO-410). */
	IDP_CODE_TAKEN = 'IDP_CODE_TAKEN',
	/** The tenant already holds the maximum number of identity providers (TO-410). */
	IDP_LIMIT_REACHED = 'IDP_LIMIT_REACHED',
	/** An attribute-mapping save named a `baseVersion` other than the current one (TO-377 REQ-002). */
	IDP_MAPPING_VERSION_CONFLICT = 'IDP_MAPPING_VERSION_CONFLICT',
	/** `expectedVersion` is not the file's current version, or a concurrent first save won (TO-391). */
	FILE_CONFIG_VERSION_CONFLICT = 'FILE_CONFIG_VERSION_CONFLICT',
	/** The tenant already has an import definition with that key (TO-414 REQ-001). */
	IMPORT_DEFINITION_KEY_TAKEN = 'IMPORT_DEFINITION_KEY_TAKEN',
	/** The definition already has a DRAFT version; `details.draftVersion` (TO-414 REQ-004). */
	IMPORT_DEFINITION_DRAFT_EXISTS = 'IMPORT_DEFINITION_DRAFT_EXISTS',
	/** `expectedRevision` is not the draft's current revision; `details.currentRevision` (TO-414 REQ-005). */
	IMPORT_DEFINITION_REVISION_CONFLICT = 'IMPORT_DEFINITION_REVISION_CONFLICT',
	/** Only a DRAFT version can be edited or activated; `details.status` (TO-414 REQ-008). */
	IMPORT_DEFINITION_VERSION_NOT_DRAFT = 'IMPORT_DEFINITION_VERSION_NOT_DRAFT',
	/** Only an ACTIVE version can be retired; `details.status` (TO-414 REQ-008). */
	IMPORT_DEFINITION_VERSION_NOT_ACTIVE = 'IMPORT_DEFINITION_VERSION_NOT_ACTIVE',
	/** A file configuration still references the version; `details.fileTypes` (TO-414 REQ-007). */
	IMPORT_DEFINITION_VERSION_IN_USE = 'IMPORT_DEFINITION_VERSION_IN_USE',
	/** Another non-deleted SFTP connector of the tenant has the same trimmed, case-folded name (TO-393). */
	CONNECTOR_NAME_TAKEN = 'CONNECTOR_NAME_TAKEN',
	/** The tenant already has the maximum number of SFTP connectors (TO-393). */
	CONNECTOR_LIMIT_REACHED = 'CONNECTOR_LIMIT_REACHED',
	/** A current logical-file configuration still names the connector; `details.usedBy` (TO-393). */
	CONNECTOR_IN_USE = 'CONNECTOR_IN_USE',
	/** A host-key confirmation before any test observed a key (TO-393). */
	HOST_KEY_NOT_OBSERVED = 'HOST_KEY_NOT_OBSERVED',
	/** The confirmed fingerprint is not the last observed one (TO-393). */
	HOST_KEY_CHANGED = 'HOST_KEY_CHANGED',
	/** The connector pins fingerprints and the confirmed one is not among them (TO-393). */
	HOST_KEY_NOT_EXPECTED = 'HOST_KEY_NOT_EXPECTED',
	/** Complete was called before the upload's object exists (TO-392 BR-10). */
	IMPORT_UPLOAD_NOT_RECEIVED = 'IMPORT_UPLOAD_NOT_RECEIVED',
	/** The stored object's size or SHA-256 differs from the declaration (TO-392 BR-10). */
	IMPORT_UPLOAD_MISMATCH = 'IMPORT_UPLOAD_MISMATCH',
	/** The import's status does not allow the receipt; `details.from` / `details.to` (TO-392). */
	IMPORT_EXECUTION_INVALID_TRANSITION = 'IMPORT_EXECUTION_INVALID_TRANSITION',
	/** The tenant already holds the maximum of 20 live outbound subscriptions (TO-381). */
	WEBHOOK_SUBSCRIPTION_LIMIT_REACHED = 'WEBHOOK_SUBSCRIPTION_LIMIT_REACHED',
	/** An outbound subscription needs the tenant's signing secret first (TO-381). */
	WEBHOOK_SIGNING_SECRET_MISSING = 'WEBHOOK_SIGNING_SECRET_MISSING',
	/** Only a failed, non-test delivery can be replayed (TO-381). */
	WEBHOOK_DELIVERY_NOT_REPLAYABLE = 'WEBHOOK_DELIVERY_NOT_REPLAYABLE',
	/** A failed delivery is replayed at most once (TO-381). */
	WEBHOOK_DELIVERY_ALREADY_REPLAYED = 'WEBHOOK_DELIVERY_ALREADY_REPLAYED',
	/** Replay needs the delivery's subscription live and enabled (TO-381). */
	WEBHOOK_SUBSCRIPTION_DISABLED = 'WEBHOOK_SUBSCRIPTION_DISABLED',
	/** The erasure subject is already anonymised (TO-232 REQ-002). */
	SUBJECT_ALREADY_ERASED = 'SUBJECT_ALREADY_ERASED',
	/** The customer has an active legal hold (TO-232 REQ-002/004/006). */
	LEGAL_HOLD_ACTIVE = 'LEGAL_HOLD_ACTIVE',
	/** The customer already has an open erasure request (TO-232 REQ-002). */
	ERASURE_ALREADY_OPEN = 'ERASURE_ALREADY_OPEN',
	/** The erasure request's status does not allow the action (TO-232 REQ-004/005/009). */
	ERASURE_INVALID_STATE = 'ERASURE_INVALID_STATE',
	/** The legal hold was already released (TO-232 REQ-006). */
	LEGAL_HOLD_ALREADY_RELEASED = 'LEGAL_HOLD_ALREADY_RELEASED',
	/** The SAR identifier matches several customers; `details.customerIds` lists them (TO-232 REQ-102). */
	SUBJECT_AMBIGUOUS = 'SUBJECT_AMBIGUOUS',
	/** The SAR bundle is still being collected (TO-232 REQ-105). */
	SAR_NOT_READY = 'SAR_NOT_READY',
	/** A concurrent request made another template the default of the same channel/locale (TO-140). */
	NOTIFICATION_TEMPLATE_DEFAULT_CONFLICT = 'NOTIFICATION_TEMPLATE_DEFAULT_CONFLICT',
}

/** Gone codes for 410 responses. */
export enum GoneErrorCode {
	/** Default: the resource existed but is no longer available. */
	GONE = 'GONE',
	/** The SAR's 24-hour download window has ended (TO-232 REQ-105). */
	SAR_EXPIRED = 'SAR_EXPIRED',
}
